/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  TextField,
  Typography,
  Breadcrumbs,
  Button,
  Alert,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigationListener } from '../components/hooks/useNavigationListener';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '@ink-ai/portal/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
  workflowActions,
  importWorkflow,
} from '@ink-ai/portal/reducers/workflow';
import { PaperCard } from '../components/Common';

export const ImportWorkflow: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const workflow = useSelector((state: RootState) => state.workflow);
  const [name, setName] = useState('');
  const [type, setType] = useState('dify');
  const [description, setDescription] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');

  useNavigationListener();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleApiEndpointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiEndpoint(e.target.value);
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value);
  };

  const handleCancel = () => {
    navigate('/workflows');
  };

  const handleImport = () => {
    dispatch(workflowActions.setLoading(true));
    dispatch(
      importWorkflow({
        name,
        type,
        description,
        apiEndpoint,
        apiKey,
      }),
    );
  };

  const isFormValid = name && apiEndpoint && apiKey;

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t('workflows.navigation.home')}</Link>
        <Link to="/workflows">{t('workflows.navigation.workflows')}</Link>
        <Typography color="textPrimary">
          {t('workflows.import.title')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left', mb: 2 }}>
          {t('workflows.import.title')}
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'right',
              width: '60%',
              border: '1px solid #2196f3',
              backgroundColor: '#fff',
              paddingRight: '40px',
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {t('workflows.import.standard.title')}
              </Typography>
              <Typography variant="body2">
                {t('workflows.import.standard.description')}
              </Typography>
            </Box>
          </Alert>
          <TextField
            label={t('workflows.import.name.label')}
            variant="outlined"
            value={name}
            onChange={handleNameChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: '60%', mt: 1 }}
          />
          <TextField
            label={t('workflows.import.apiEndpoint.label')}
            variant="outlined"
            value={apiEndpoint}
            onChange={handleApiEndpointChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: '60%', mt: 2 }}
            placeholder="https://api.example.com/v1"
          />
          <TextField
            label={t('workflows.import.apiKey.label')}
            variant="outlined"
            type="text"
            value={apiKey}
            onChange={handleApiKeyChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: '60%', mt: 2 }}
            placeholder="app-xxxxxxxxxxxxxxxxxxxx"
          />
          <TextField
            label={t('workflows.import.description.label')}
            variant="outlined"
            value={description}
            onChange={handleDescriptionChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: '60%', mt: 2 }}
            multiline
            rows={3}
          />
          <Box
            sx={{
              mt: 2,
              textAlign: 'left',
            }}
          >
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={!isFormValid}
              sx={{ width: '99px' }}
              loading={workflow.loading}
            >
              {t('workflows.import.buttons.import')}
            </LoadingButton>
            <Button onClick={handleCancel} sx={{ ml: 1, width: '99px' }}>
              {t('workflows.import.buttons.cancel')}
            </Button>
          </Box>
        </Box>
      </PaperCard>
    </Container>
  );
};
